// =============================================================================
// csvInvestmentParser.js — Phase 65.1a
//
// Parses brokerage CSV exports (Vanguard OfxDownload, Fidelity, Schwab, IBKR,
// generic broker) into a normalized shape the Investment Import Dialog can
// consume.
//
// Output shape:
//   {
//     detected_format: 'vanguard_csv' | 'fidelity_csv' | 'schwab_csv' | ...,
//     detected_currency: 'USD' | 'INR' | ...,
//     statement_as_of: '2025-06-30' | null,
//     holdings: [{
//       id, kind, name, symbol, units, value, cost_basis, currency,
//       account_hint, confidence, raw
//     }],
//     activity: [{
//       // Classified rows we WILL create as transactions on commit.
//       id, kind: 'income' | 'expense', category, description, amount,
//       currency, txn_date, raw
//     }],
//     activity_stats: {
//       total_rows,     // total rows found in the transactions section
//       dividends,      // classified as income (dividends)
//       interest,       // classified as income (interest)
//       fees,           // classified as expense (broker fees)
//       skipped_internal, // buys/sells/reinvestments — no cash-flow impact
//       unknown,        // rows we couldn't confidently classify
//     },
//     warnings: string[],
//   }
//
// Why client-side (not backend)?
//   • CSVs are already structured — no LLM cost, no round-trip.
//   • Preview UX is instant.
//   • Backend still records the audit trail on commit via commit-csv.
// =============================================================================
import Papa from 'papaparse';

// ---------------------------------------------------------------------------
// Header keyword sets — mirrors CSVImport.js so multi-section detection is
// consistent between the two entry points. Not exported directly (we prefer
// small pure functions over shared globals).
// ---------------------------------------------------------------------------
const HOLDINGS_KEYS = [
  'symbol', 'ticker', 'shares', 'units', 'quantity',
  'market value', 'total value', 'value', 'cost basis', 'price',
  'share price',
];
const TXN_KEYS = [
  'trade date', 'transaction date', 'settlement date', 'posted', 'posting date',
  'transaction type', 'transaction description', 'amount', 'net amount',
  'principal amount', 'debit', 'credit', 'description',
];
const HEADER_HINTS = [...new Set([...HOLDINGS_KEYS, ...TXN_KEYS])];

function normalize(s) {
  return String(s || '').trim().toLowerCase();
}

function countHeaderHits(row) {
  if (!Array.isArray(row)) return 0;
  let hits = 0;
  for (const c of row) {
    const n = normalize(c);
    if (!n) continue;
    if (HEADER_HINTS.some((k) => n === k || n.includes(k))) hits += 1;
  }
  return hits;
}

function isHoldingsHeader(row) {
  const cells = row.map(normalize);
  const hasHoldingSignal = cells.some((c) => HOLDINGS_KEYS.some((k) => c.includes(k)));
  const hasTxnSignal     = cells.some((c) => TXN_KEYS.some((k) => c === k || c.includes(k)));
  return hasHoldingSignal && !hasTxnSignal;
}

function detectAllHeaderRows(allRows, { minKeywords = 3 } = {}) {
  const out = [];
  for (let i = 0; i < allRows.length; i += 1) {
    const r = allRows[i];
    if (!Array.isArray(r)) continue;
    const hits = countHeaderHits(r);
    if (hits >= minKeywords) out.push({ index: i, row: r, hits });
  }
  return out;
}

// ---------------------------------------------------------------------------
// Column mapping — for each canonical field we may need, list the header
// synonyms in priority order. First match wins.
// ---------------------------------------------------------------------------
const HOLDINGS_COLUMN_MAP = {
  symbol:     ['symbol', 'ticker', 'security symbol'],
  name:       ['investment name', 'security name', 'description', 'name', 'security description'],
  units:      ['shares', 'quantity', 'units', 'qty', 'share count'],
  price:      ['share price', 'price', 'market price', 'current price'],
  value:      ['total value', 'market value', 'current value', 'value'],
  cost_basis: ['cost basis', 'total cost', 'book value'],
  currency:   ['currency', 'ccy'],
  account:    ['account number', 'account', 'account id'],
};

const ACTIVITY_COLUMN_MAP = {
  txn_date:    ['trade date', 'transaction date', 'posted date', 'posting date', 'date'],
  description: ['transaction description', 'description', 'memo', 'transaction'],
  txn_type:    ['transaction type', 'type', 'activity'],
  symbol:      ['symbol', 'ticker'],
  security:    ['investment name', 'security name', 'name'],
  amount:      ['net amount', 'amount', 'principal amount'],   // Net > Principal (Net includes fees)
  fees:        ['commissions and fees', 'commission', 'fees'],
  currency:    ['currency', 'ccy'],
  account:     ['account number', 'account'],
};

function findColumn(headerRow, aliases) {
  const cells = headerRow.map(normalize);
  for (const alias of aliases) {
    const idx = cells.indexOf(alias);
    if (idx !== -1) return idx;
  }
  // Fallback — substring match (e.g. "Investment Name" contains "name")
  for (const alias of aliases) {
    const idx = cells.findIndex((c) => c.includes(alias));
    if (idx !== -1) return idx;
  }
  return -1;
}

function buildColumnIndex(headerRow, columnMap) {
  const out = {};
  for (const [field, aliases] of Object.entries(columnMap)) {
    out[field] = findColumn(headerRow, aliases);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Value coercion — tolerant of "$1,234.56", "1,234.56 USD", "(1,234.56)"
// (parenthesized negatives), and blank cells.
// ---------------------------------------------------------------------------
function parseAmount(raw) {
  if (raw === null || raw === undefined) return null;
  const t = String(raw).trim();
  if (!t) return null;
  const negParen = /^\(.*\)$/.test(t);
  const cleaned = t.replace(/[()$,\s]/g, '').replace(/[A-Za-z]/g, '');
  const n = parseFloat(cleaned);
  if (!isFinite(n)) return null;
  return negParen ? -Math.abs(n) : n;
}

function parseDate(raw) {
  if (!raw) return null;
  const t = String(raw).trim();
  // ISO first — Vanguard uses this.
  const iso = t.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (iso) {
    return `${iso[1]}-${iso[2].padStart(2, '0')}-${iso[3].padStart(2, '0')}`;
  }
  // MM/DD/YYYY (US brokers)
  const us = t.match(/^(\d{1,2})[/\-](\d{1,2})[/\-](\d{2,4})/);
  if (us) {
    let [, m, d, y] = us;
    if (y.length === 2) y = (parseInt(y, 10) > 50 ? '19' : '20') + y;
    return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Kind inference — best-effort mapping from CSV symbol/name to our
// wealth_holdings.kind enum. Users can override in the review UI.
// ---------------------------------------------------------------------------
function inferKind(symbol, name) {
  const s = normalize(symbol);
  const n = normalize(name);
  if (/(bond|treasury|note|debenture)/.test(n)) return 'bond';
  if (/(gold|silver|precious metal)/.test(n)) return 'gold';
  if (/(fund|mutual|index|etf)/.test(n)) return 'mutual_fund';
  if (/\bcd\b|certificate of deposit/.test(n)) return 'deposit';
  if (s && /^[A-Z]{1,5}$/.test((symbol || '').trim())) return 'stock';   // NYSE/NASDAQ ticker shape
  return 'other';
}

// ---------------------------------------------------------------------------
// Format detection — used only for cosmetic labelling in the review UI.
// ---------------------------------------------------------------------------
function detectFormat(headerCells, allRowsSample) {
  const cells = headerCells.map(normalize);
  if (cells.includes('investment name') && cells.includes('share price')) return 'vanguard_csv';
  if (cells.includes('trade date') && cells.includes('settlement date') && cells.includes('symbol')) {
    return 'vanguard_csv';
  }
  const asText = allRowsSample.map((r) => Array.isArray(r) ? r.join(' ') : String(r)).join(' ').toLowerCase();
  if (asText.includes('vanguard')) return 'vanguard_csv';
  if (asText.includes('fidelity')) return 'fidelity_csv';
  if (asText.includes('schwab'))   return 'schwab_csv';
  if (asText.includes('interactive brokers') || asText.includes('ibkr')) return 'ibkr_csv';
  return 'broker_csv';
}

// ---------------------------------------------------------------------------
// Activity classifier — decides what a transaction row means for cash flow.
//
// Rule of thumb:
//   • Buy / Sell / Reinvest → internal move, NO cash-flow impact → SKIP
//     (we may later use these to reconstruct cost basis)
//   • Dividend / Interest   → real income → transactions.type='income'
//   • Fee / Commission      → real expense → transactions.type='expense'
//   • Deposit / Contribution → transfer INTO account (not income; skip for now
//     to avoid double-counting with the sending bank statement)
//   • Withdrawal            → transfer OUT of account (skip, same reason)
//   • Anything else         → skip with a "unknown" tally so we can iterate
// ---------------------------------------------------------------------------
function classifyActivity(txnTypeRaw, descriptionRaw, amountRaw) {
  const type = normalize(txnTypeRaw);
  const desc = normalize(descriptionRaw);
  const amt  = amountRaw == null ? 0 : Number(amountRaw);

  // Dividends — highest confidence signal.
  if (/dividend/.test(type) || /dividend/.test(desc)) {
    if (/reinvest/.test(type) || /reinvest/.test(desc)) {
      return { classification: 'skip_internal', reason: 'dividend_reinvested' };
    }
    return {
      classification: 'income',
      category: 'Investment Income',
      subcategory: 'Dividends',
    };
  }
  // Interest income
  if (/interest/.test(type) || /interest/.test(desc)) {
    return {
      classification: 'income',
      category: 'Investment Income',
      subcategory: 'Interest',
    };
  }
  // Capital gains distributions (fund distributions)
  if (/capital gain/.test(type) || /capital gain/.test(desc)) {
    return {
      classification: 'income',
      category: 'Investment Income',
      subcategory: 'Capital Gains',
    };
  }
  // Broker fees / commissions
  if (/fee|commission|charge|expense/.test(type) || /fee|commission|charge/.test(desc)) {
    return {
      classification: 'expense',
      category: 'Fees & Charges',
      subcategory: 'Investment Fees',
    };
  }
  // Internal — buys, sells, reinvests, exchanges.
  if (/buy|purchase/.test(type)) return { classification: 'skip_internal', reason: 'buy' };
  if (/sell|redemption|redeem/.test(type)) return { classification: 'skip_internal', reason: 'sell' };
  if (/reinvest/.test(type)) return { classification: 'skip_internal', reason: 'reinvest' };
  if (/exchange|swap|split|stock split/.test(type)) return { classification: 'skip_internal', reason: 'exchange' };
  if (/transfer/.test(type)) return { classification: 'skip_internal', reason: 'transfer' };
  // Deposits / contributions — treat as transfers (skip to avoid double count).
  if (/deposit|contribution|funding/.test(type)) return { classification: 'skip_internal', reason: 'deposit' };
  if (/withdrawal|distribution/.test(type)) return { classification: 'skip_internal', reason: 'withdrawal' };
  return { classification: 'unknown', reason: 'unrecognized_type' };
}

// ===========================================================================
// Main entry point
// ===========================================================================
export function parseInvestmentCsv(fileText, { filename = 'investments.csv' } = {}) {
  const parsed = Papa.parse(fileText, { header: false, skipEmptyLines: true });
  const allRows = parsed.data || [];
  const warnings = [];

  if (allRows.length === 0) {
    return _empty({ warnings: ['File is empty.'] });
  }

  const candidates = detectAllHeaderRows(allRows, { minKeywords: 3 });
  if (candidates.length === 0) {
    return _empty({ warnings: ['We couldn’t find a header row we recognized.'] });
  }

  const holdingsHeader = candidates.find((c) => isHoldingsHeader(c.row));
  const txnHeader      = candidates.find((c) => !isHoldingsHeader(c.row));

  const detected_format = detectFormat(
    (holdingsHeader || txnHeader).row,
    allRows.slice(0, Math.min(30, allRows.length)),
  );
  const detected_currency = _inferCurrency(fileText, filename);
  const statement_as_of = _extractStatementDate(allRows);

  // ---- Holdings extraction --------------------------------------------------
  let holdings = [];
  if (holdingsHeader) {
    const idx = buildColumnIndex(holdingsHeader.row, HOLDINGS_COLUMN_MAP);
    // Slice from just after the header row, and stop at the next detected
    // header (which is either the transactions header, or EOF).
    const endIdx = txnHeader && txnHeader.index > holdingsHeader.index
      ? txnHeader.index
      : allRows.length;
    const body = allRows.slice(holdingsHeader.index + 1, endIdx);
    holdings = body.map((row, i) => _makeHolding(row, idx, {
      currency: detected_currency,
      raw_row_num: holdingsHeader.index + 1 + i + 1,   // 1-indexed for humans
    })).filter(Boolean);
    if (holdings.length === 0) {
      warnings.push('Found a holdings section but no data rows in it.');
    }
  }

  // ---- Activity extraction + classification --------------------------------
  let activity = [];
  const activity_stats = {
    total_rows:       0,
    dividends:        0,
    interest:         0,
    capital_gains:    0,
    fees:             0,
    skipped_internal: 0,
    skipped_transfer: 0,
    unknown:          0,
  };
  if (txnHeader) {
    const idx = buildColumnIndex(txnHeader.row, ACTIVITY_COLUMN_MAP);
    const body = allRows.slice(txnHeader.index + 1);
    for (let i = 0; i < body.length; i += 1) {
      const row = body[i];
      if (!Array.isArray(row) || row.every((c) => !normalize(c))) continue;
      activity_stats.total_rows += 1;
      const raw = _extractRawFields(row, idx);
      const cls = classifyActivity(raw.txn_type, raw.description, raw.amount);
      if (cls.classification === 'skip_internal') {
        if (cls.reason === 'deposit' || cls.reason === 'withdrawal' || cls.reason === 'transfer') {
          activity_stats.skipped_transfer += 1;
        } else {
          activity_stats.skipped_internal += 1;
        }
        continue;
      }
      if (cls.classification === 'unknown') {
        activity_stats.unknown += 1;
        continue;
      }
      // Real cash-flow row → build a transaction candidate.
      const amt = Math.abs(Number(raw.amount) || 0);
      if (amt <= 0) {
        // Zero-amount rows (e.g. bookkeeping entries) — skip silently.
        activity_stats.skipped_internal += 1;
        continue;
      }
      const txnDate = parseDate(raw.txn_date);
      if (!txnDate) {
        activity_stats.unknown += 1;
        continue;
      }
      // Bucket counters
      if (cls.subcategory === 'Dividends')     activity_stats.dividends     += 1;
      if (cls.subcategory === 'Interest')      activity_stats.interest      += 1;
      if (cls.subcategory === 'Capital Gains') activity_stats.capital_gains += 1;
      if (cls.subcategory === 'Investment Fees') activity_stats.fees        += 1;

      activity.push({
        id: `csv-act-${i}`,
        kind: cls.classification,   // 'income' | 'expense'
        category: cls.category,
        subcategory: cls.subcategory,
        description: _composeActivityDescription(raw, cls),
        amount: amt,
        currency: raw.currency || detected_currency,
        txn_date: txnDate,
        raw,
      });
    }
    if (activity_stats.total_rows > 0 && activity.length === 0) {
      warnings.push('The transactions section only contained internal moves (buys/sells) — nothing new to add to your cash flow.');
    }
  }

  return {
    detected_format,
    detected_currency,
    statement_as_of,
    holdings,
    activity,
    activity_stats,
    warnings,
  };
}

// ---------------------------------------------------------------------------
// Row-level helpers
// ---------------------------------------------------------------------------
function _makeHolding(row, idx, { currency, raw_row_num }) {
  const symbol = idx.symbol >= 0 ? String(row[idx.symbol] || '').trim() : '';
  const name   = idx.name   >= 0 ? String(row[idx.name]   || '').trim() : '';
  if (!symbol && !name) return null;

  const units = idx.units >= 0 ? parseAmount(row[idx.units]) : null;
  const price = idx.price >= 0 ? parseAmount(row[idx.price]) : null;
  let value   = idx.value >= 0 ? parseAmount(row[idx.value]) : null;
  const cost  = idx.cost_basis >= 0 ? parseAmount(row[idx.cost_basis]) : null;

  // If we have units + price but no value, compute it.
  if ((value == null || value === 0) && units != null && price != null) {
    value = Math.round(units * price * 100) / 100;
  }

  // Drop rows that have no meaningful numeric signal.
  if ((units == null || units === 0) && (value == null || value === 0)) {
    return null;
  }

  return {
    id: `csv-hld-${raw_row_num}`,
    kind: inferKind(symbol, name),
    name: name || symbol,
    symbol: symbol || null,
    isin: null,
    units,
    value: value == null ? 0 : value,
    cost_basis: cost,
    currency: (idx.currency >= 0 ? String(row[idx.currency] || '').trim().toUpperCase() : null) || currency,
    account_hint: idx.account >= 0 ? String(row[idx.account] || '').trim() : null,
    confidence: 'high',
    raw: { row: row.slice(0, 20) },
  };
}

function _extractRawFields(row, idx) {
  const get = (k) => idx[k] >= 0 ? row[idx[k]] : null;
  return {
    txn_type:    get('txn_type'),
    txn_date:    get('txn_date'),
    description: get('description'),
    symbol:      get('symbol'),
    security:    get('security'),
    amount:      parseAmount(get('amount')),
    fees:        parseAmount(get('fees')),
    currency:    idx.currency >= 0 ? String(row[idx.currency] || '').trim().toUpperCase() || null : null,
    account:     get('account'),
  };
}

function _composeActivityDescription(raw, cls) {
  const bits = [];
  if (cls.subcategory) bits.push(cls.subcategory);
  if (raw.symbol)   bits.push(String(raw.symbol).trim());
  else if (raw.security) bits.push(String(raw.security).trim());
  if (bits.length === 0 && raw.description) bits.push(String(raw.description).trim());
  return bits.filter(Boolean).join(' · ').slice(0, 200) || 'Investment activity';
}

function _inferCurrency(fileText, filename) {
  const sample = (fileText || '').slice(0, 2000);
  if (/\bUSD\b|\$/.test(sample)) return 'USD';
  if (/\bINR\b|₹/.test(sample)) return 'INR';
  if (/\bEUR\b|\u20ac/.test(sample)) return 'EUR';
  if (/\bGBP\b|\u00a3/.test(sample)) return 'GBP';
  // Filename-based fallback for Vanguard exports (usually USD).
  if (/vanguard|fidelity|schwab|ibkr/i.test(filename)) return 'USD';
  return 'USD';
}

function _extractStatementDate(rows) {
  // Look for an "as of" line in the first 5 rows.
  for (let i = 0; i < Math.min(5, rows.length); i += 1) {
    const text = (rows[i] || []).join(' ').toLowerCase();
    const m = text.match(/as of[:\s]+(\d{4}-\d{1,2}-\d{1,2}|\d{1,2}[/\-]\d{1,2}[/\-]\d{2,4})/);
    if (m) {
      const d = parseDate(m[1]);
      if (d) return d;
    }
  }
  return null;
}

function _empty(overrides) {
  return {
    detected_format: 'unknown',
    detected_currency: 'USD',
    statement_as_of: null,
    holdings: [],
    activity: [],
    activity_stats: {
      total_rows: 0, dividends: 0, interest: 0, capital_gains: 0,
      fees: 0, skipped_internal: 0, skipped_transfer: 0, unknown: 0,
    },
    warnings: [],
    ...overrides,
  };
}

// Convenience: is the parser output usable at all?
export function hasImportableContent(result) {
  if (!result) return false;
  return (result.holdings || []).length > 0 || (result.activity || []).length > 0;
}
